import React, { useState } from "react";
import { Field, Form, Formik } from "formik";

import { BaseDropdown, FileUpload, LargeButton, OverlayLoader, BaseInput } from "components/core";
import useNavigateInsuranceSetup from "hooks/useNavigateInsuranceSetup";
import http from "services/httpService";
import { uploadFile, rupeesToNumber } from "utlis/utils";
import { gpaGtlInitialValues, gpaGtlSchema } from "./schemas";
import { Divider, Divv, DividerOr } from "./styles";
import TextInput from "./TextInput";

const GpaGtlForm = ({ type, details }) => {
  const [isLoading, setIsLoading] = useState(false);
  //keys to re-render file component
  const [key, setKey] = useState(0);
  // custom hook to handle insurance product navigation
  const { navigateInsurancePlan } = useNavigateInsuranceSetup();
  // storing file
  const [file, setFile] = useState();
  // storing file error state
  const [fileError, setFileError] = useState(false);

  // handle file error
  const handleError = () => {
    if (!file) setFileError(true);
  };

  // resetting state and form after form submission
  const resetState = (resetForm) => {
    // resetting state
    setFile(null);
    setFileError(false);
    // resetting form
    resetForm();
  };

  // handle form submission
  const handleSubmit = async (
    values,
    { setSubmitting, setTouched, resetForm }
  ) => {
    // exiting if no files
    var fileId
    if (file) {
      fileId = file && (await uploadFile(file, "premiums_excel"))
    }
    // setting touched to true to display errors if errors persist
    setTouched(
      Object.keys(values).reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {})
    );
    // uploading file and storing the file_id
    setIsLoading(true)
    // const premiumdId = fileId && (await uploadRateChart(fileId))
    // calling API to upload the values
    const resp = await setupGpaGtl(fileId, values, type);
    if(resp === false) return;
    const marked_products = localStorage.getItem("marked_products");
    // setting index based on product type
    const index = type === "GTL" ? 2 : type === "GPA" ? 1 : null;
    if (!marked_products) {
      // creating a new local storage and storing completed index in an array
      localStorage.setItem("marked_products", JSON.stringify([index]));
    } else {
      // appending the new index if marked_products already exist
      const parsed = JSON.parse(marked_products);
      localStorage.setItem(
        "marked_products",
        JSON.stringify([...parsed, index])
      );
    }
    // changing key to re-render file component
    setKey(key + 1);
    // navigating to next page
    navigateInsurancePlan();
    resetState(resetForm);
    setSubmitting(false);
  };

  const uploadRateChart = async (fileId) => {
    const rateChartData = new FormData();

    rateChartData.append("document_id", fileId);
    rateChartData.append("gmc_type", "annual");

    const options = {
      "Content-Type": "multipart/form-data",
    };

    const { data } = await http.post("/insurance/setup_gmc_premiums/", rateChartData, options);

    return data.premium_id;
  };

  // save gpa/gtl setup
  const setupGpaGtl = async (
    premium_id,
    // { insurer: insurer_name, sum_insured },
    { insurer: insurer_name, sum_insured, commission, amount_per_mil },
    type
  ) => {
    // getting business user id
    const business_user_id = localStorage.getItem("id");
    // getting userplan_ids -> it would be different for different plans
    const userplan_ids = localStorage.getItem("userplan_ids");
    if(sum_insured == 'FLAT'){
      sum_insured = rupeesToNumber(commission)
    }
    if(amount_per_mil){
      amount_per_mil = rupeesToNumber(amount_per_mil)
    }
    try {
      let { data } = await http.post("/business/setupplan/", {
        business_type: type, // GPA or GTL
        business_user_id,
        insurer_name,
        premium_id, // file id of the document uploaded
        sum_insured,
        amount_per_mil
      });

      if (!userplan_ids) {
        // creating a new local storage for userplan_ids and storing the strigified value
        localStorage.setItem(
          "userplan_ids",
          JSON.stringify([data.userplan_id])
        );
      } else {
        // adding the new userplan_id if it already exist
        const parsed = JSON.parse(userplan_ids);
        localStorage.setItem(
          "userplan_ids",
          JSON.stringify([...parsed, data.userplan_id])
        );
      }
    } catch (err) {
      alert(err.response.data.error)
      return false;
    } finally {
      setIsLoading(false)
    }
  };

  const getDropdownOptions = (selectedInsurer, field) => {
    let options = [];
    // returning empty array if insurer is not selected
    if (!selectedInsurer) {
      return options;
    } else {
      // setting option to the respective fields
      options = getOptions(details[field][selectedInsurer]);
    }

    return options;
  };

  const getOptions = (details) => {
    if (!details) return [];
    return details.map(({ display_name, value }) => ({
      label: display_name,
      value,
    }));
  };

  return (
    <>
    {
      isLoading? (<OverlayLoader/>
    ): (
    <Formik
      enableReinitialize={true}
      initialValues={gpaGtlInitialValues}
      validationSchema={gpaGtlSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          <div style={{ marginBottom: "2.2rem" }}>
            <BaseDropdown
              fontSize="14"
              label="Insurer"
              options={getOptions(details["insurers"])}
              formikProps={{ ...formikProps, name: "insurer" }}
            />
          </div>
          <div style={{ marginBottom: "2.2rem" }}>
            <BaseDropdown
              fontSize="14"
              label="Sum Insured"
              options={getOptions(details["sum_insured"])}
              formikProps={{ ...formikProps, name: "sum_insured" }}
            />
          </div>
          <div style={{ marginBottom: "2.2rem", marginTop: "2rem" }}>
            {(formikProps && 
              formikProps.values && 
              formikProps.values.sum_insured == 'FLAT') &&
              <BaseInput
                label="Sum Insured Amount"
                type="text"
                formatToRupee
                formikProps={{ ...formikProps, name: "commission" }}
              />
            }
          </div>
          <div style={{ margin: "4rem 0rem" }}>
            <Divider />
          </div>
          <FileUpload
            key={key}
            label="Upload Rate Chart"
            text="Upload Excel"
            onSelect={setFile}
            // showError={fileError}
            bottom="15.8rem"
          />
          <div style={{ marginTop: "4rem" }}>
            <LargeButton
              type="submit"
              label="Save & Next"
              marginTop="2rem"
              onClick={handleError}
            />
          </div>
        </Form>
      )}
    </Formik>)}
    </>
  );
};

export default GpaGtlForm;
